import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';

import {
  SCANNER_FILTERS, SCAN_STATE, applyFilter, sortCandidates, triggeredLevelLabel,
  signalActionLabel, isBullish, allSignals,
} from '../utils/marketScanner';

const TIER_COLOR = { HIGH: 'success', MEDIUM: 'warning', WATCH: 'default' };

/**
 * THE REVIEW DIALOG: the candidates one scan produced, and a click that loads one.
 *
 * <h2>Selecting a row LOADS THE SYMBOL AND DRAWS BOTH ENGINES</h2>
 *
 * That is the point of the dialog — a candidate is a claim about a level, and the only way to
 * judge it is to see the level. The handler is given the symbol and the caller wires it to the
 * same cycle-preset overlays the toolbar draws, so the lines a reader checks against are the
 * lines the chart already knows how to draw rather than a second rendering of them.
 *
 * <h2>It stays OPEN on selection</h2>
 *
 * A reviewer works down a list, and a dialog that closed on every click would make comparing
 * two candidates a sequence of reopenings. The chart redraws underneath; the dialog is not
 * modal-blocking, so it can be read against the chart it just changed.
 */
const ScannerCandidatesDialog = ({
  open, onClose, candidates, status, state, onSelectSymbol, selectedSymbol,
  scanDate, onScanDateChange, onRerun, latestSession,
}) => {
  const [filterId, setFilterId] = useState('all');

  const rows = useMemo(
    () => sortCandidates(applyFilter(candidates, filterId)),
    [candidates, filterId],
  );

  // The counts live on the PILLS so a reader can see an empty filter before clicking it —
  // "Bullish Bounces (0)" is a finding, where an empty table after a click is a dead end.
  const counts = useMemo(() => {
    const out = {};
    SCANNER_FILTERS.forEach((f) => {
      out[f.id] = (Array.isArray(candidates) ? candidates : []).filter(f.test).length;
    });
    return out;
  }, [candidates]);

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="lg" fullWidth hideBackdrop
      sx={{ '& .MuiDialog-paper': { maxHeight: '82vh' } }}
      data-testid="scanner-candidates-dialog"
    >
      <DialogTitle sx={{ pb: 1, fontSize: 16, fontWeight: 700 }}>
        Scanner Candidates
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          Price interacting with a PTA or PTB level within 0.25%. Click a row to load that symbol
          with both engines drawn.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
        {status && (
          // Severity from the STATE ENUM, never by sniffing the prose. The first version read
          // `status.startsWith('The scan failed')`, which makes the colour of an error depend on
          // the exact wording of a sentence -- so rewording a message silently turns a failure
          // blue.
          <Alert severity={state === SCAN_STATE.FAILED ? 'error'
            : (state === SCAN_STATE.COMPLETE ? 'success' : 'info')}
            sx={{ mb: 1.5, py: 0, whiteSpace: 'pre-line' }}
            data-testid="scanner-status">
            {status}
          </Alert>
        )}

        {/* ── THE DATE, AND WHY IT IS HERE RATHER THAN ONLY IN THE ADMIN PANEL ──────────────
            The toolbar button takes no parameters, so without this a reader who gets an empty
            result has no way to ask a different question -- and the commonest reason for an
            empty result is a scan date the data does not reach. Measured on this machine: the
            default resolved to today, the EOD store ended seven days earlier, and 446 of 447
            symbols reported "no bar". The counts said so and there was nothing to do about it.

            Re-run rather than live-on-change: a scan is ~900ms over 447 symbols and reads every
            file, so firing it on each keystroke of a date would be several scans to type one. */}
        {onRerun && (
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center' }}>
            <TextField
              size="small" type="date" label="Scan date" value={scanDate || ''}
              onChange={(e) => onScanDateChange && onScanDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'scanner-dialog-date' }}
              sx={{ width: 170 }}
            />
            <Button variant="outlined" size="small" data-testid="scanner-dialog-rerun"
              disabled={state === SCAN_STATE.LOADING || state === SCAN_STATE.SCANNING}
              onClick={() => onRerun(scanDate || undefined)}>
              {state === SCAN_STATE.SCANNING ? 'Scanning…' : 'Re-run'}
            </Button>
            {latestSession && (
              // ⚠ NAMED, not implied. "0 with signals" and "the data does not reach that date"
              // read identically on screen, and only one of them is worth retrying.
              <Typography variant="caption" sx={{ color: 'text.secondary' }}
                data-testid="scanner-latest-session">
                {`latest session on the chart: ${latestSession}`}
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {SCANNER_FILTERS.map((f) => (
            <Chip
              key={f.id}
              label={`${f.label} (${counts[f.id] ?? 0})`}
              size="small"
              color={filterId === f.id ? 'primary' : 'default'}
              variant={filterId === f.id ? 'filled' : 'outlined'}
              onClick={() => setFilterId(f.id)}
              data-testid={`scanner-pill-${f.id}`}
            />
          ))}
        </Stack>

        {rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            {(candidates || []).length === 0
              ? 'No candidates. Either the scan found no interactions, or no band lines were in '
                + 'force on that date — lines exist only between generation intervals.'
              : 'No candidates match this filter.'}
          </Typography>
        ) : (
          // RESPONSIVE: the container scrolls on its own rather than the dialog, so the pills
          // and the header stay put while a long list is read.
          <TableContainer sx={{ maxHeight: '52vh', overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Asset Class</TableCell>
                  <TableCell align="right">Close</TableCell>
                  <TableCell>Triggered Level</TableCell>
                  <TableCell>Signal Action</TableCell>
                  <TableCell>Confluence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const bullish = isBullish(row);
                  const isSelected = selectedSymbol && row.symbol === selectedSymbol;
                  return (
                    <TableRow
                      key={`${row.market}_${row.symbol}`}
                      hover
                      selected={!!isSelected}
                      onClick={() => onSelectSymbol && onSelectSymbol(row.symbol, row)}
                      sx={{ cursor: 'pointer' }}
                      data-testid={`scanner-row-${row.symbol}`}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.symbol}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {row.assetClass}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {Number.isFinite(row.closePrice) ? row.closePrice.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell>{triggeredLevelLabel(row)}</TableCell>
                      <TableCell>
                        {/* Colour AND words. Colour alone says nothing to a colour-blind
                            reader or in a monochrome screenshot. */}
                        <Typography variant="body2" sx={{
                          color: bullish === null ? 'text.primary'
                            : (bullish ? 'success.main' : 'error.main'),
                          fontWeight: 600,
                        }}>
                          {signalActionLabel(row)}
                        </Typography>
                        {allSignals(row).length > 1 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {allSignals(row).length} signals
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.confluenceTier}
                          color={TIER_COLOR[row.confluenceTier] || 'default'}
                          variant={row.confluenceTier === 'WATCH' ? 'outlined' : 'filled'} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {rows.length} of {(candidates || []).length} shown.
            {' '}HIGH means both engines fired on the same bar; MEDIUM is two or more lines of one
            engine; WATCH is a single line.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

ScannerCandidatesDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  candidates: PropTypes.array,
  status: PropTypes.string,
  /** The date the next run will ask for, as yyyy-MM-dd. */
  scanDate: PropTypes.string,
  onScanDateChange: PropTypes.func,
  /** Re-runs the scan. ABSENT hides the date row entirely — see the note at its render. */
  onRerun: PropTypes.func,
  /** The last session the loaded chart has, shown so an empty result is diagnosable. */
  latestSession: PropTypes.string,
  state: PropTypes.string,
  onSelectSymbol: PropTypes.func,
  selectedSymbol: PropTypes.string,
};

export default ScannerCandidatesDialog;
